import { useState, useCallback, useEffect } from "react";
import { validateEmail } from "@/lib/validation/email-validation";
import { validateAndProcessEmail, saveNewEmail, type GameAttempt } from "@/services/emailService";
import { supabase } from "@/integrations/supabase/client";

export function useEmailHandler(
  onComplete: (email: string) => void,
  onExistingResult?: (attemptData: GameAttempt) => void
) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState("");

  const handleStoredEmail = useCallback(async (storedEmail: string) => {
    setIsLoading(true);
    try {
      // If already stored, we assume it's verified
      const { existingAttempt, isExistingUser, error } = await validateAndProcessEmail(storedEmail);
      
      if (error) {
        console.error('Error processing stored email:', error);
        console.log('Stored email processing error details:', { storedEmail, error });
        return;
      }

      if (existingAttempt && onExistingResult) {
        onExistingResult(existingAttempt);
      } else {
        onComplete(storedEmail);
      }
    } catch (error) {
      console.error('Error processing stored email:', error);
      console.log('Stored email error details:', { storedEmail, error });
    } finally {
      setIsLoading(false);
    }
  }, [onComplete, onExistingResult]);

  // Listen for auth changes from magic link
  useEffect(() => {
    console.log('Setting up auth state listener for magic link verification');
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.email);
      
      if ((event === 'SIGNED_IN' || event === 'USER_UPDATED') && session) {
        // User clicked the magic link and was signed in
        console.log('User signed in via magic link:', session.user.email);
        setIsVerifying(false);
        
        const userEmail = session.user.email;
        if (userEmail) {
          // Save email to localStorage
          localStorage.setItem("user_email", userEmail);
          
          // If email isn't set in component state (e.g., coming directly from link), set it
          if (!email) {
            setEmail(userEmail);
          }
          
          // Continue to the game - use timeout to ensure state updates complete
          setTimeout(() => {
            onComplete(userEmail);
          }, 50);
        }
      }
    });
    
    return () => {
      console.log('Cleaning up auth state listener');
      subscription.unsubscribe();
    };
  }, [email, onComplete]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validateEmail(email);
    if (!validation.valid) {
      setVerificationError(validation.reason || "Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    
    try {
      // First, save the email to the database
      await saveNewEmail(email);
      
      // Check if it's an existing user
      const { isExistingUser, existingAttempt } = await validateAndProcessEmail(email);
      
      // Save email to localStorage right away
      localStorage.setItem("user_email", email);
      
      if (isExistingUser && existingAttempt && onExistingResult) {
        // If there's an existing attempt, pass it along
        onExistingResult(existingAttempt);
      } else {
        // Otherwise, just proceed directly without verification
        onComplete(email);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setVerificationError("An error occurred. Please try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendLink = async () => {
    // This is now a no-op since we're bypassing verification
    setNeedsVerification(false);
    if (email) {
      localStorage.setItem("user_email", email);
      onComplete(email);
    }
  };

  return {
    email,
    setEmail,
    isLoading,
    handleStoredEmail,
    handleSubmit,
    needsVerification,
    setNeedsVerification,
    isVerifying,
    verificationError,
    handleResendLink
  };
}
